import type { PlacementEntry } from "./specs";

/**
 * Demo zone prop layout. Coordinates are in world metres.
 *
 *   - X / Z = position on the island plane.
 *   - Y     = ground level. Each prop's *base* lands on this Y automatically
 *             (see ASSET_CATALOG in props.ts), so for flat sand: just leave it 0.
 *             Bump Y slightly (e.g. 0.05) for things partly buried in sand if
 *             we don't want them flush.
 *
 *   - scale = per-instance MULTIPLIER on the asset's natural size.
 *             1.0 = catalog default. 1.15 = 15% taller. Use this only for
 *             organic variation between siblings of the same asset.
 */
export const PLACEMENTS: PlacementEntry[] = [
  // Hub building roughly at island centre
  { model: "sanctuary_hut.glb", pos: [0, 0, -8], rotY: 0.2, collide: true, tag: "hut" },

  // Palm grove around the hut — vary scale ±10% for organic look
  { model: "palm_tree.glb", pos: [-12, 0, -2],  rotY: 0.5,  scale: 1.05, collide: true, tag: "palm_a" },
  { model: "palm_tree.glb", pos: [10, 0, -3],   rotY: -0.7, scale: 0.95, collide: true, tag: "palm_b" },
  { model: "palm_tree.glb", pos: [-8, 0, 8],    rotY: 1.4,  scale: 1.10, collide: true, tag: "palm_c" },
  { model: "palm_tree.glb", pos: [14, 0, 9],    rotY: 2.1,  scale: 0.90, collide: true, tag: "palm_d" },
  { model: "palm_tree.glb", pos: [-22, 0, -14], rotY: 0.0,  scale: 1.00, collide: true, tag: "palm_e" },
  { model: "palm_tree.glb", pos: [22, 0, -10],  rotY: 1.0,  scale: 0.95, collide: true, tag: "palm_f" },

  // Rocks scattered along the shoreline
  { model: "rock_cluster.glb", pos: [-30, 0, 4],  rotY: 0.3,  scale: 1.10, collide: true, tag: "rocks_n" },
  { model: "rock_cluster.glb", pos: [28, 0, 14],  rotY: -1.2, scale: 0.90, collide: true, tag: "rocks_e" },
  { model: "rock_cluster.glb", pos: [-4, 0, 26],  rotY: 2.0,  scale: 1.20, collide: true, tag: "rocks_s" },

  // Driftwood pieces (still missing — will skip silently until you drop the GLB)
  { model: "driftwood_log.glb", pos: [-18, 0, 18], rotY: 0.9,  scale: 1.0, collide: true, tag: "drift_a" },
  { model: "driftwood_log.glb", pos: [16, 0, -22], rotY: -0.4, scale: 0.9, collide: true, tag: "drift_b" },

  // Beach grass tufts (no collider, decorative)
  { model: "beach_grass.glb", pos: [3, 0, 5],    rotY: 0.2, scale: 1.0, tag: "grass_a" },
  { model: "beach_grass.glb", pos: [-5, 0, -3],  rotY: 1.1, scale: 0.9, tag: "grass_b" },
  { model: "beach_grass.glb", pos: [8, 0, -6],   rotY: 2.4, scale: 1.1, tag: "grass_c" },
  { model: "beach_grass.glb", pos: [-9, 0, 12],  rotY: 0.7, scale: 0.8, tag: "grass_d" },
  { model: "beach_grass.glb", pos: [12, 0, 4],   rotY: 1.8, scale: 1.0, tag: "grass_e" },

  // Conch shells (collectibles in a future patch). Slight Y nudge to peek
  // out of the sand without floating.
  { model: "conch_shell.glb", pos: [-15, 0.02, 22], rotY: 0.5,  scale: 1.0, tag: "shell_a" },
  { model: "conch_shell.glb", pos: [20, 0.02, 18],  rotY: -1.3, scale: 0.85, tag: "shell_b" },
  { model: "conch_shell.glb", pos: [4, 0.02, 28],   rotY: 2.0,  scale: 1.10, tag: "shell_c" },

  // Wooden crates near the hut (interactable later)
  { model: "wooden_crate.glb", pos: [3.5, 0, -6],  rotY: 0.4,  scale: 1.0, collide: true, tag: "crate_a" },
  { model: "wooden_crate.glb", pos: [-2.5, 0, -10], rotY: -0.3, scale: 0.9, collide: true, tag: "crate_b" },

  // Baby turtles scattered around the central plaza so the player meets them
  // immediately on spawn. Wander AI keeps each within a small roam radius from
  // its spawn position (see TURTLE_WANDER_CONFIG in main.ts).
  { model: "baby_turtle.glb", pos: [4, 0, 4],     rotY: 1.8, scale: 1.0, tag: "turtle_a" }, // Fred
  { model: "baby_turtle.glb", pos: [-3, 0, 6],    rotY: 2.5, scale: 1.0, tag: "turtle_b" }, // Shellbert
  { model: "baby_turtle.glb", pos: [-5, 0, -2],   rotY: 0.4, scale: 0.85, tag: "turtle_c" }, // Crunkles (smol)
  { model: "baby_turtle.glb", pos: [7, 0, -4],    rotY: 1.1, scale: 1.20, tag: "turtle_d" }, // Trevor (lv99)
  { model: "baby_turtle.glb", pos: [-9, 0, 14],   rotY: 0.9, scale: 1.30, tag: "turtle_e" }, // Big Mama
  { model: "baby_turtle.glb", pos: [9, 0, 16],    rotY: 2.7, scale: 0.70, tag: "turtle_f" }, // Squirt (hatchling)
  { model: "baby_turtle.glb", pos: [-14, 0, 6],   rotY: 1.5, scale: 0.95, tag: "turtle_g" }, // Yoshi
  { model: "baby_turtle.glb", pos: [13, 0, 6],    rotY: -0.6, scale: 1.00, tag: "turtle_h" }, // Donatello
];
